/* eslint-disable no-restricted-syntax */
/* eslint-disable guard-for-in */
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
import axios from 'axios';
import { UnprocessableEntityError } from './errors/unprocessableEntityError';
import { ForbiddenError } from './errors/forbiddenError';
import { ServerError } from './errors/serverError';

const axiosInstance = axios.create({});
const CREATE_VEHICLE_DUPLICATE_ERROR = "You're about to add a car that's already in Vindat, do you want to continue?";
const EMAIL_IN_USE = 'Email in use';
export const INCOMING_CREDIT_LIMIT_NOT_ENOUGH = "INCOMING_CREDIT_LIMIT_NOT_ENOUGH";
export const VAT_DUPLICATE_MESSAGE = 'A company with this VAT number already exists.';

axiosInstance.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    console.log('err: ', error.response);
    if (error.response.status === 403 && error.response?.data?.message === CREATE_VEHICLE_DUPLICATE_ERROR) {
      throw error;
      // return Promise.resolve({ data: { message: error.response.statusText }, result: error.response.data?.data?.payload });
    } else if (error.response.status === 403 && error.response?.data?.message === EMAIL_IN_USE) {
      return Promise.resolve({ data: { message: error.response.data.message }, status: error.response.status });
    } else if (error.response.status === 403 && error.response?.data?.message === INCOMING_CREDIT_LIMIT_NOT_ENOUGH) {
      return Promise.resolve({ data: { message: error.response.data.message }, status: error.response.status });
    } else {
      return Promise.reject(error);
    }
  },
);

function parseJSON(response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json();
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export default function request(url, options) {
  return fetch(url, options)
    .then(checkStatus)
    .then(parseJSON);
}

function responseHandler(response) {
  if (response.status >= 200 && response.status < 300) return response;
  if (response.status === 401) throw 'Unauthorized';
  if (response.status === 422) {
    throw new UnprocessableEntityError(response.data.message, response.data.errors);
  }
  if (response.status === 403 && response.data.message === EMAIL_IN_USE) {
    return response;
  }

  if (response.status === 403) {
    if (response?.data?.message == CREATE_VEHICLE_DUPLICATE_ERROR) {
      return response;
    }
    if (response?.data?.message == INCOMING_CREDIT_LIMIT_NOT_ENOUGH) {
      return response;
    }
    throw new ForbiddenError(response.data.message);
  }

  if (response.status >= 500 && response.status <= 599) {
    throw new ServerError(response.data.message);
  }

  return false;
}

function handleFormDataArgs(...args) {
  let [url, data, options] = args;
  if (options?.formData === true) {
    delete options.formData;
    options.headers = { ...(options.headers ?? {}), 'Content-Type': 'multipart/form-data' };
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }

    return [url, formData, options];
  }

  return [url, data, options];
}

export function POST(url, data, options) {
  [url, data, options] = handleFormDataArgs(url, data, options);

  return axiosInstance
    .post(url, data, options)
    .then(responseHandler)
    .catch((a, b, c) => {
      if (a && a.response && a.response.status) return responseHandler(a.response);
      return false;
    });
}

export function GET(url, options) {
  return axios
    .get(url, options)
    .then(responseHandler)
    .catch(function(a) {
      if (a && a.response && a.response.status) return responseHandler(a.response);
      return false;
    });
}

export function DELETE(url, options, data) {
  const deleteOptions = data ? { ...options, data } : options;
  console.log('DELETE: ', deleteOptions);
  return axios
    .delete(url, deleteOptions)
    .then(responseHandler)
    .catch(function(a) {
      if (a && a.response && a.response.status) return responseHandler(a.response);
      return false;
    });
}

export function PUT(url, data, options) {
  return axios
    .put(url, data, options)
    .then(responseHandler)
    .catch((a, b, c) => {
      if (a && a.response && a.response.status) return responseHandler(a.response);
      return false;
    });
}

export function PATCH(url, data, options) {
  return axios
    .patch(url, data, options)
    .then(responseHandler)
    .catch((a, b, c) => {
      // Quick fix. Refactor later
      if (a?.response?.request?.responseURL.includes('/api/v1/invoice')) {
        return responseHandler(a.response);
      }

      if (a?.response?.request?.responseURL.includes('/api/v1/workflows')) {
        return responseHandler(a.response);
      }

      //this specific signature is an server error that we can  pass along the the user
      if (a?.response?.data?.success === false && a?.response?.data?.message) {
        const { data } = a?.response;
        let response = {
          result: false,
          data: {
            message: data?.message,
          },
        };

        if (data?.message === VAT_DUPLICATE_MESSAGE) {
          response.data.matchedCompaniesIds = data?.data?.payload?.matchedCompaniesIds;
        }

        return response;
      }

      if (a && a.response && a.response.status) {
        return responseHandler(a.response);
      }

      return false;
    });
}
